import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Link } from 'react-router-dom';

const { FiHeart, FiShield, FiCoffee, FiHome, FiArrowRight } = FiIcons;

export default function FamilyCaregiverHub() {
  const { contentLanguage } = useTranslation();

  const sections = [
    {
      title: { en: 'Daily Care Logistics', el: 'Εφοδιαστική Καθημερινής Φροντίδας' },
      description: { en: 'Managing medicines, appointments, and home safety.', el: 'Διαχείριση φαρμάκων, ραντεβού και ασφάλειας στο σπίτι.' },
      icon: FiHome,
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: { en: 'Emotional Support', el: 'Συναισθηματική Υποστήριξη' },
      description: { en: 'Coping with stress and finding balance.', el: 'Αντιμετώπιση του στρες και εξεύρεση ισορροπίας.' },
      icon: FiHeart,
      color: 'bg-rose-50 text-rose-600'
    },
    {
      title: { en: 'Self-Care for You', el: 'Αυτοφροντίδα για Εσάς' },
      description: { en: 'Remembering your own health while caring for others.', el: 'Θυμηθείτε τη δική σας υγεία ενώ φροντίζετε άλλους.' },
      icon: FiCoffee,
      color: 'bg-amber-50 text-amber-600'
    },
    {
      title: { en: 'Legal & Financial', el: 'Νομικά & Οικονομικά' },
      description: { en: 'Understanding rights, benefits, and support.', el: 'Κατανόηση δικαιωμάτων, παροχών και υποστήριξης.' },
      icon: FiShield,
      color: 'bg-emerald-50 text-emerald-600'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="relative rounded-3xl overflow-hidden bg-gray-900 text-white p-12 mb-16">
        <div className="relative z-10 max-w-2xl">
          <h1 className="text-4xl md:text-5xl font-black mb-6">
            {contentLanguage === 'el' ? 'Κέντρο Φροντιστών & Οικογένειας' : 'Family & Caregiver Hub'}
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            {contentLanguage === 'el' 
              ? 'Είμαστε εδώ για να υποστηρίξουμε αυτούς που υποστηρίζουν. Πρακτική καθοδήγηση για κάθε βήμα της διαδρομής.' 
              : 'We are here to support those who support. Practical guidance for every step of the journey.'}
          </p>
        </div>
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-20 bg-gradient-to-l from-blue-500 to-transparent pointer-events-none" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {sections.map((section, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all flex items-start gap-6"
          >
            <div className={`p-4 rounded-2xl ${section.color}`}>
              <SafeIcon icon={section.icon} className="text-3xl" />
            </div>
            <div className="flex-grow">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {section.title[contentLanguage]}
              </h3>
              <p className="text-gray-600 mb-6 text-lg">
                {section.description[contentLanguage]}
              </p>
              <Link 
                to="/resources"
                className="inline-flex items-center gap-2 font-bold text-blue-600 hover:text-blue-700 transition-colors"
              >
                <span>{contentLanguage === 'el' ? 'Δείτε τους πόρους' : 'View resources'}</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-16 bg-blue-600 rounded-3xl p-12 text-white flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="max-w-xl text-center md:text-left">
          <h2 className="text-3xl font-black mb-4">
            {contentLanguage === 'el' ? 'Έχετε μια συμβουλή να μοιραστείτε;' : 'Have a tip to share?'}
          </h2>
          <p className="text-blue-100 text-lg">
            {contentLanguage === 'el' 
              ? 'Οι εμπειρίες σας μπορούν να βοηθήσουν άλλες οικογένειες. Γίνετε εθελοντής σήμερα.' 
              : 'Your experiences can help other families. Become a volunteer today.'}
          </p>
        </div>
        <Link 
          to="/submit"
          className="px-8 py-4 bg-white text-blue-600 font-black rounded-2xl hover:bg-gray-100 transition-all shadow-lg"
        >
          {contentLanguage === 'el' ? 'Υποβολή Συμβουλής' : 'Submit a Tip'}
        </Link>
      </div>
    </div>
  );
}